import React, { useRef, useState, useEffect } from "react";
import {
    Card,
    CardBody,
    Container,
    Row,
    Col,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Alert
} from "reactstrap";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate, useParams } from "react-router-dom";
import Api from "../../components/helpers/Api";
import { SeoHelper } from "../../components/helpers/SeoHelper";
import { sessionEnded } from "../user/SessionEnded";

const DEFAULT_BASE_URL = "https://theworshipbible.church";

/**
 * Final GA-safe normalisation (save-time / blur-time)
 */
const normaliseCampaign = (value) =>
    (value || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9_-]+/g, "_")
        .replace(/[_-]{2,}/g, "_")
        .replace(/^[_-]+|[_-]+$/g, "");

/**
 * Gentle typing-time sanitiser
 * Allows: letters, numbers, space, _ and -
 */
const sanitiseWhileTyping = (value) =>
    (value || "")
        .toLowerCase()
        .replace(/[^a-z0-9 _-]/g, "");

export default function QrCampaignEdit() {
    const navigate = useNavigate();
    const { id } = useParams();
    const qrRef = useRef(null);

    const [form, setForm] = useState({
        baseUrl: DEFAULT_BASE_URL,
        utmSource: "",
        utmMedium: "",
        utmCampaign: "",
        utmContent: "",
        utmTerm: "",
        intendedUse: "",
        notes: ""
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const customer = sessionStorage.getItem("customer");
        if (!customer || !JSON.parse(customer).user?.isAdmin) {
            navigate("/");
            return;
        }

        Api.get(`/api/admin/qr-campaigns/${id}`)
            .then(res => {
                setForm({
                    baseUrl: res.data.baseUrl || DEFAULT_BASE_URL,
                    utmCampaign: res.data.utmCampaign || "",
                    utmSource: res.data.utmSource || "",
                    utmMedium: res.data.utmMedium || "",
                    utmContent: res.data.utmContent || "",
                    utmTerm: res.data.utmTerm || "",
                    intendedUse: res.data.intendedUse || "",
                    notes: res.data.notes || ""
                });
            })
            .catch(() => setError("Failed to load campaign"))
            .finally(() => setLoading(false));

    }, [id, navigate]);

    const buildUrl = () => {
        const params = new URLSearchParams();

        Object.entries(form).forEach(([key, value]) => {
            if (key !== "baseUrl" && value) {
                params.append(
                    key.replace(/[A-Z]/g, m => "_" + m.toLowerCase()),
                    value
                );
            }
        });

        return `${form.baseUrl}?${params.toString()}`;
    };

    const saveChanges = async () => {
        setSaving(true);
        setError(null);
        setSuccess(false);

        try {
            await Api.put(`/api/admin/qr-campaigns/${id}`, {
                ...form,
                utmCampaign: normaliseCampaign(form.utmCampaign)
            });

            setSuccess(true);
        } catch (err) {
            setError(
                err?.response?.data?.message ||
                "Failed to save changes"
            );
        } finally {
            setSaving(false);
        }
    };

    if (!sessionStorage.getItem("customer")) {
        return sessionEnded();
    }

    if (loading) {
        return (
            <Container className="py-5 text-center">
                Loading campaign…
            </Container>
        );
    }

    const normalisedPreview = normaliseCampaign(form.utmCampaign);

    return (
        <>
            <SeoHelper noindex />

            <div className="position-relative">
                {/* HERO */}
                <section className="section section-lg section-shaped pb-250">
                    <div className="shape shape-style-1 shape-default">
                        <span /><span /><span /><span /><span /><span /><span /><span /><span />
                    </div>
                    <Container className="py-lg-md d-flex">
                        <div className="col px-0">
                            <Row>
                                <Col lg="8">
                                    <h1 className="display-3 text-white">
                                        Marketing tools<br />
                                        <span>edit QR campaign</span>
                                    </h1>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                    <div className="separator separator-bottom separator-skew">
                        <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                            <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                        </svg>
                    </div>
                </section>

                {/* MAIN PANEL */}
                <section className="section section-lg pt-lg-0 mt--200">
                    <Container>
                        <Row className="justify-content-center">
                            <Col lg="12">
                                <Card className="shadow border-0">
                                    <CardBody className="py-5">

                                        <Button
                                            color="link"
                                            className="mb-4 p-0"
                                            onClick={() => navigate("/admin/marketing")}
                                        >
                                            &larr; Back to campaigns
                                        </Button>

                                        {error && <Alert color="danger">{error}</Alert>}
                                        {success && <Alert color="success">Changes saved successfully.</Alert>}

                                        <Form>
                                            <Row>
                                                {/* FORM */}
                                                <Col md={6}>

                                                    <FormGroup>
                                                        <Label>Base URL</Label>
                                                        <Input
                                                            value={form.baseUrl}
                                                            onChange={e => setForm({ ...form, baseUrl: e.target.value })}
                                                        />
                                                        <small className="text-muted">
                                                            The URL that the QR code will point to.
                                                        </small>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>UTM Campaign <b>(Required)</b></Label>
                                                        <Input
                                                            value={form.utmCampaign}
                                                            onChange={e =>
                                                                setForm({
                                                                    ...form,
                                                                    utmCampaign: sanitiseWhileTyping(e.target.value)
                                                                })
                                                            }
                                                            onBlur={() =>
                                                                setForm({
                                                                    ...form,
                                                                    utmCampaign: normaliseCampaign(form.utmCampaign)
                                                                })
                                                            }
                                                        />
                                                        <small className="text-muted">
                                                            Normalised automatically (recommended for GA).<br />
                                                            Saved as:{" "}
                                                            <code className="text-primary">
                                                                {normalisedPreview || "—"}
                                                            </code>
                                                            <br />
                                                            e.g. "Lent 2026 Flyer" becomes "lent_2026_flyer"
                                                        </small>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>UTM Source</Label>
                                                        <Input
                                                            value={form.utmSource}
                                                            onChange={e => setForm({ ...form, utmSource: e.target.value })}
                                                        />
                                                        <small className="text-muted">
                                                            e.g. facebook, email, instagram, linkedin
                                                        </small>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>UTM Medium</Label>
                                                        <Input
                                                            value={form.utmMedium}
                                                            onChange={e => setForm({ ...form, utmMedium: e.target.value })}
                                                        />
                                                        <small className="text-muted">
                                                            e.g. social, paid_social, print, qr, email
                                                        </small>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>UTM Content</Label>
                                                        <Input
                                                            value={form.utmContent}
                                                            onChange={e => setForm({ ...form, utmContent: e.target.value })}
                                                        />
                                                        <small className="text-muted">
                                                            e.g. reel_short, story_video, carousel_slide_1
                                                        </small>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>UTM Term</Label>
                                                        <Input
                                                            value={form.utmTerm}
                                                            onChange={e => setForm({ ...form, utmTerm: e.target.value })}
                                                        />
                                                        <small className="text-muted">
                                                            Used for audience or targeting details.
                                                        </small>
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>Intended Use</Label>
                                                        <Input
                                                            value={form.intendedUse}
                                                            onChange={e => setForm({ ...form, intendedUse: e.target.value })}
                                                        />
                                                    </FormGroup>

                                                    <FormGroup>
                                                        <Label>Notes</Label>
                                                        <Input
                                                            type="textarea"
                                                            value={form.notes}
                                                            onChange={e => setForm({ ...form, notes: e.target.value })}
                                                        />
                                                    </FormGroup>

                                                </Col>

                                                {/* PREVIEW */}
                                                <Col md={6} className="text-center">
                                                    <div
                                                        ref={qrRef}
                                                        className="bg-white p-4 rounded shadow-sm d-inline-block"
                                                        style={{ position: "relative" }}
                                                    >
                                                        <QRCodeCanvas
                                                            value={buildUrl()}
                                                            size={260}
                                                            level="H"
                                                            includeMargin
                                                            fgColor="#5E72E4"
                                                            bgColor="#ffffff"
                                                        />

                                                        <img
                                                            src="/qr/worship-bible-qr-logo.png"
                                                            alt="Worship Bible"
                                                            style={{
                                                                position: "absolute",
                                                                top: "50%",
                                                                left: "50%",
                                                                width: 64,
                                                                height: 64,
                                                                transform: "translate(-50%, -50%)",
                                                                background: "white",
                                                                padding: 6,
                                                                borderRadius: 8
                                                            }}
                                                        />
                                                    </div>

                                                    <p className="small mt-3 text-muted text-break">
                                                        {buildUrl()}
                                                    </p>

                                                    <div className="d-flex justify-content-center gap-2 mt-3">
                                                        <Button
                                                            color="primary"
                                                            onClick={saveChanges}
                                                            disabled={saving}
                                                        >
                                                            {saving ? "Saving…" : "Save Changes"}
                                                        </Button>
                                                    </div>
                                                </Col>

                                            </Row>
                                        </Form>

                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </div>
        </>
    );
}